import { ActionIcon, Tooltip } from "@mantine/core";
import { IconCheck, IconCopy } from "@tabler/icons-react";
import { useState } from "react";
import { copyMarkdownToClipboard } from "../utils/copyToClipboard";

interface CopyToClipboardButtonProps {
    content?: string;
    onCopy?: () => Promise<void>;
    tooltip?: string;
    size?: "xs" | "sm" | "md" | "lg" | "xl";
    variant?: "subtle" | "light" | "filled" | "outline" | "default" | "transparent";
    color?: string;
    disabled?: boolean;
    className?: string;
}

export const CopyToClipboardButton = ({
    content,
    onCopy,
    tooltip = "Copy to clipboard",
    size = "sm",
    variant = "subtle",
    color = "gray",
    disabled = false,
    className,
}: CopyToClipboardButtonProps) => {
    const [copied, setCopied] = useState(false);

    const handleCopy = async () => {
        if (disabled) return;
        if (!onCopy && !content) return;

        try {
            if (onCopy) {
                await onCopy();
            } else if (content) {
                await copyMarkdownToClipboard(content);
            }
            setCopied(true);

            // Reset copied state after 2 seconds
            setTimeout(() => setCopied(false), 2000);
        } catch (error) {
            console.error("Failed to copy content:", error);
        }
    };

    return (
        <Tooltip label={copied ? "Copied!" : tooltip} position="top" withArrow>
            <ActionIcon
                size={size}
                variant={variant}
                color={copied ? "green" : color}
                onClick={handleCopy}
                disabled={disabled || (!content && !onCopy)}
                className={className}
                aria-label={tooltip}
            >
                {copied ? <IconCheck size={16} /> : <IconCopy size={16} />}
            </ActionIcon>
        </Tooltip>
    );
};
